import Phaser from 'phaser';

/**
 * World Map — top-down เลื่อนแนวตั้ง สไตล์ Stardew/Pokémon
 * พื้นหญ้า tile จริง + ทางหินกรวด + ต้นไม้แน่น + อาคารไทยเป็นหมุด + ตัวเดิน
 */
const VIEW_W = 340;
const VIEW_H = 480;
const BUILDINGS = ['pavilion', 'city', 'house', 'cave', 'market', 'temple'];
const NATURE = ['tree', 'cherry', 'banyan', 'bamboo', 'rock'];

export default class WorldMapScene extends Phaser.Scene {
  constructor(data) {
    super('WorldMap');
    this._data = data || { pins: [], totalKm: 0 };
  }

  preload() {
    this.load.image('grass', '/assets/tiles/grass.png');
    this.load.image('cobble', '/assets/tiles/cobble.png');
    const c = this._data.chosenChar || 'king';
    this.load.image('hero', c === 'king' ? '/assets/thai_char.png' : `/assets/characters/${c}.png`);
    this.load.image('crown', '/assets/items/crown.png');
    for (const b of BUILDINGS) this.load.image(b, `/assets/buildings/${b}.png`);
    for (const t of NATURE) this.load.image(t, `/assets/nature/${t}.png`);
  }

  create() {
    const pins = this._data.pins || [];
    const n = Math.max(pins.length, 1);
    this._maxKm = pins.length ? Math.max(...pins.map((p) => p.target || 0)) : 1;

    const GAP = 178, MB = 110, MT = 96;
    this._worldH = MB + (n - 1) * GAP + MT;
    this._nodes = pins.map((p, i) => ({
      ...p,
      x: 170 + 84 * Math.sin(i * 1.25),
      y: this._worldH - MB - i * GAP,
      building: BUILDINGS[i % BUILDINGS.length],
    }));

    // พื้นหญ้าปู tile จริง
    this.add.tileSprite(0, 0, VIEW_W, this._worldH, 'grass').setOrigin(0, 0).setDepth(-12);

    this._drawClouds();
    this._drawPath();
    this._drawDecorations();

    // อาคารประจำหมุด — ใหญ่กว่าต้นไม้ (เป็นพระเอก) + มงกุฎเมืองที่พิชิต
    this._nodes.forEach((nd, i) => {
      if (i === 0) this._waterShimmer(nd.x, nd.y + 4);   // กรุงเทพฯ ริมน้ำ
      const b = this.add.image(nd.x, nd.y, nd.building).setOrigin(0.5, 0.82).setDepth(nd.y);
      b.setScale(128 / b.width);
      if (!nd.unlocked) {
        b.setTint(0x6f7a70); b.setAlpha(0.55);
      } else {
        const cr = this.add.image(nd.x, nd.y - 102, 'crown').setDepth(nd.y + 1);
        cr.setScale(30 / cr.width);
        this.tweens.add({ targets: cr, y: cr.y - 6, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
      }
      const t = this.add.text(nd.x, nd.y + 24, nd.name,
        { fontFamily: 'Noto Sans Thai', fontSize: '14px', color: '#2f4a34', fontStyle: 'bold' }).setOrigin(0.5).setDepth(9000);
      t.setStroke('#fff8e6', 5);
      this.add.text(nd.x, nd.y + 41, `${nd.target} กม.`,
        { fontFamily: 'Noto Sans Thai', fontSize: '10px', color: '#5a4a2a' }).setOrigin(0.5).setStroke('#fff8e6', 4).setDepth(9000);
    });

    // ตัวละคร
    this._targetFrac = clamp((this._data.totalKm || 0) / this._maxKm, 0, 1);
    this._charFrac = 0;
    const start = this._posAtFrac(0);
    this._nong = this.add.sprite(start.x, start.y, 'hero').setOrigin(0.5, 0.95).setDepth(9500);
    this._baseScale = 74 / this._nong.height;
    this._nong.setScale(this._baseScale);
    this._prevX = start.x;

    // กล้อง + สกอลล์
    const cam = this.cameras.main;
    cam.setBounds(0, 0, VIEW_W, this._worldH);
    cam.scrollY = clamp(this._posAtFrac(this._targetFrac).y - VIEW_H * 0.6, 0, this._worldH - VIEW_H);
    this._setupScroll(cam);
  }

  _setupScroll(cam) {
    const maxScroll = Math.max(0, this._worldH - VIEW_H);
    let dragY = null;
    this.input.on('pointerdown', (p) => { dragY = p.y; });
    this.input.on('pointerup', () => { dragY = null; });
    this.input.on('pointerupoutside', () => { dragY = null; });
    this.input.on('pointermove', (p) => {
      if (dragY !== null) { cam.scrollY = clamp(cam.scrollY - (p.y - dragY), 0, maxScroll); dragY = p.y; }
    });
    this.input.on('wheel', (_p, _o, _dx, dy) => { cam.scrollY = clamp(cam.scrollY + dy * 0.5, 0, maxScroll); });
  }

  update(time) {
    this._charFrac += (this._targetFrac - this._charFrac) * 0.03;
    if (Math.abs(this._targetFrac - this._charFrac) < 0.001) this._charFrac = this._targetFrac;
    const moving = Math.abs(this._targetFrac - this._charFrac) > 0.003;
    const pos = this._posAtFrac(this._charFrac);
    const hop = moving ? Math.abs(Math.sin(time * 0.012)) * 7 : Math.sin(time * 0.005) * 1.5;
    this._nong.setPosition(pos.x, pos.y - hop);
    if (moving) {
      const dx = pos.x - this._prevX;
      if (Math.abs(dx) > 0.05) this._nong.setFlipX(dx < 0);
      this._nong.setRotation(Math.sin(time * 0.012) * 0.06);
      this._nong.setScale(this._baseScale, this._baseScale * (1 + Math.sin(time * 0.024) * 0.05));
    } else {
      this._nong.setRotation(0); this._nong.setScale(this._baseScale);
    }
    this._prevX = pos.x;
  }

  _posAtFrac(frac) {
    const km = clamp(frac, 0, 1) * this._maxKm;
    const nd = this._nodes;
    if (!nd.length) return { x: 170, y: 0 };
    if (km <= nd[0].target) return { x: nd[0].x, y: nd[0].y };
    for (let i = 0; i < nd.length - 1; i++) {
      if (km >= nd[i].target && km <= nd[i + 1].target) {
        const t = (km - nd[i].target) / ((nd[i + 1].target - nd[i].target) || 1);
        return { x: nd[i].x + (nd[i + 1].x - nd[i].x) * t, y: nd[i].y + (nd[i + 1].y - nd[i].y) * t };
      }
    }
    const last = nd[nd.length - 1];
    return { x: last.x, y: last.y };
  }

  _drawPath() {
    const nd = this._nodes;
    for (let i = 0; i < nd.length - 1; i++) {
      const a = nd[i], b = nd[i + 1];
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      const steps = Math.max(1, Math.round(dist / 18));
      for (let s = 0; s <= steps; s++) {
        const t = s / steps;
        const x = a.x + (b.x - a.x) * t;
        const y = (a.y - 6) + ((b.y - 6) - (a.y - 6)) * t;
        const c = this.add.image(x, y, 'cobble').setDepth(-5);
        c.setScale(34 / c.width);
      }
    }
  }

  _drawDecorations() {
    let k = 0;
    for (let y = 56; y < this._worldH - 24; y += 62) {
      this._prop(22 + (k % 3) * 6, y + (k % 2) * 8, k);
      this._prop(318 - (k % 3) * 6, y + ((k + 1) % 2) * 8, k + 1);
      if (k % 2 === 0) this._prop((k % 4 < 2) ? 66 : 274, y + 22, k + 2);   // กระจุกกลางทุ่ง (เลี่ยงทางเดิน)
      k++;
    }
  }

  _prop(x, y, k) {
    const key = NATURE[(k * 3 + (k % 2)) % NATURE.length];
    const img = this.add.image(x, y, key).setOrigin(0.5, 1).setDepth(y);
    img.setScale((40 + (k % 3) * 7) / img.width);
    if (key !== 'rock') {   // ต้นไม้/ไผ่ไหวเบา ๆ
      this.tweens.add({ targets: img, angle: { from: -1.6, to: 1.6 }, duration: 1600 + (k % 5) * 200, yoyo: true, repeat: -1, ease: 'Sine.inOut', delay: (k % 7) * 130 });
    }
  }

  _waterShimmer(x, y) {
    for (let i = 0; i < 4; i++) {
      const s = this.add.ellipse(x - 24 + i * 16, y + (i % 2) * 4, 12, 4, 0xffffff, 0.6).setDepth(1);
      this.tweens.add({ targets: s, alpha: { from: 0.12, to: 0.55 }, scaleX: { from: 0.5, to: 1.3 }, duration: 1100 + i * 260, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    }
  }

  _drawClouds() {
    for (let i = 0; i < 6; i++) {
      const cy = 70 + i * (this._worldH / 6) + (i % 2) * 30;
      const cx = (i % 2 ? 56 : 264) + Math.random() * 30;
      const c = this.add.container(cx, cy).setDepth(8000).setAlpha(0.42);
      const g = this.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillEllipse(0, 0, 64, 30); g.fillEllipse(-26, 9, 40, 24);
      g.fillEllipse(28, 8, 42, 24); g.fillEllipse(6, -9, 34, 22);
      c.add(g);
      this.tweens.add({ targets: c, x: cx + (i % 2 ? 50 : -50), duration: 8000 + i * 900, yoyo: true, repeat: -1, ease: 'Sine.inOut' });
    }
  }
}

function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

export { VIEW_W as MAP_W, VIEW_H as MAP_H };
